import React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';

const formSchema = z.object({
  name: z.string().min(2, {
    message: 'Knowledge Base name must be at least 2 characters.',
  }),
  description: z.string().optional(),
  isPrivate: z.boolean().optional(),
});

interface KnowledgeBaseCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: z.infer<typeof formSchema>) => Promise<void>;
  categoryLabel?: string | null;
  uploadAfterCreate?: boolean;
  setUploadAfterCreate?: (uploadAfterCreate: boolean) => void;
}

const KnowledgeBaseCreateDialog = ({
  open,
  onOpenChange,
  onSubmit,
  categoryLabel,
  uploadAfterCreate = true,
  setUploadAfterCreate,
}: KnowledgeBaseCreateDialogProps) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
      isPrivate: false,
    },
  });

  React.useEffect(() => {
    if (open) {
      form.reset({
        name: '',
        description: '',
        isPrivate: false,
      });
    }
  }, [form, open, categoryLabel]);

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    await onSubmit(values);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden border-border/70 bg-background/95 p-0 shadow-2xl sm:max-w-[560px]">
        <DialogHeader className="border-b border-border/60 px-6 py-5">
          <DialogTitle>Create Knowledge Base</DialogTitle>
          <DialogDescription>
            {categoryLabel
              ? `Create a new knowledge base in ${categoryLabel}.`
              : 'Create a new knowledge base to store your documents.'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5 px-6 py-5">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="My Knowledge Base" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="A brief description of the knowledge base"
                      className="min-h-[120px] resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isPrivate"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-xl border border-border/70 bg-muted/20 p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Private</FormLabel>
                    <FormDescription>
                      Make this knowledge base private
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            {setUploadAfterCreate && (
              <div className="flex items-start gap-3 rounded-xl border border-border/70 bg-muted/20 p-4">
                <Checkbox
                  id="upload-after-create"
                  checked={uploadAfterCreate}
                  onCheckedChange={(checked) => setUploadAfterCreate(Boolean(checked))}
                />
                <div className="space-y-1">
                  <label htmlFor="upload-after-create" className="text-sm font-medium">
                    Add source documents after creating
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Open the source upload dialog immediately after the knowledge base is created.
                  </p>
                </div>
              </div>
            )}
            <DialogFooter className="border-t border-border/60 px-0 pt-5">
              <Button type="submit">Create</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default KnowledgeBaseCreateDialog;
